import {
  Alert,
  AlertTitle,
  Button,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ExternalProjectImportEntity,
  ExternalProjectImportPreview,
} from '@provision/entity-types';
import { useMemo } from 'react';
import {
  IMPORT_MORE,
  useDtoolsImport,
} from 'src/components/ImportDtoolsProjectModal/dtools-import-context';
import { ImportJobDialog } from 'src/components/ImportDtoolsProjectModal/import-job-dialog';
import { ProjectPreview } from 'src/components/ImportDtoolsProjectModal/project-preview';
import { ModalStep, useModalStep, useModalSteps } from 'src/components/ModalContainer';

const countEntities = (records: ExternalProjectImportPreview[] | undefined) => {
  const counts = {
    Project: 0,
    Site: 0,
    Client: 0,
  };

  const bucketEntity = (entity: ExternalProjectImportEntity) => {
    counts[entity.entity] += 1;
  };

  (records || []).forEach((record) => {
    record.created?.forEach(bucketEntity);
    record.updated?.forEach(bucketEntity);
  });

  return counts;
};

/** Step 2: what the dry run would create and update; opens while the dry run still runs. */
export const ProjectsSummary = () => {
  const { pushStep } = useModalSteps();
  const { popStep } = useModalStep();
  const {
    previewProjects: projects,
    previewOutcome: preview,
    isStartingJob,
    hasImportStarted,
    importPreviewed,
    clearImport,
    close,
  } = useDtoolsImport();
  const projectIds = projects.map((p) => p.id.toString());
  const projectNamesMap = useMemo(
    () => new Map(projects.map((p) => [p.id.toString(), p.name])),
    [projects],
  );
  const { records, errors, hasMoreResults, loadMoreResults } = preview;
  const loading = preview.loading || !preview.isFinished;

  const handleImport = async () => {
    if (!(await importPreviewed())) {
      return;
    }
    const result = await pushStep(<ImportJobDialog />);
    if (result === IMPORT_MORE) {
      clearImport();
      popStep(IMPORT_MORE);
    }
  };

  const counts = countEntities(records);

  const content = (() => {
    if (loading) {
      return (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height="1.5rem" width="40%" />
          <Skeleton variant="rounded" height="5rem" />
          <Skeleton variant="rounded" height="5rem" />
        </Stack>
      );
    }

    if (!records?.length) {
      return (
        <Stack spacing={1}>
          <Typography variant="subtitle2">No preview available</Typography>
          <Typography variant="body2" color="text.secondary">
            Could not generate a preview for the selected projects.
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        {errors?.length ? (
          <Alert severity="warning">
            <AlertTitle sx={{ marginBottom: 0 }}>
              Failed to get following project(s)
            </AlertTitle>
            <List dense sx={{ paddingY: 0 }}>
              {(errors || []).map((e) => (
                <ListItem key={e.projectId}>
                  <ListItemText
                    primary={projectNamesMap.get(e.projectId) ?? e.projectId}
                    secondary={e.error}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        ) : null}
        {(records || []).map((r, index) => (
          <ProjectPreview {...r} key={r.id ?? `${r.projectName}-${index}`} />
        ))}
        {hasMoreResults ? (
          <Button onClick={loadMoreResults} sx={{ alignSelf: 'flex-start' }}>
            Show more
          </Button>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {`Summary: ${counts.Site} ${counts.Site === 1 ? 'site' : 'sites'}, ${counts.Client} ${
            counts.Client === 1 ? 'client' : 'clients'
          }, ${counts.Project} ${counts.Project === 1 ? 'project' : 'projects'}`}
        </Typography>
      </Stack>
    );
  })();

  return (
    <ModalStep
      title="Confirm import"
      actions={[
        {
          label: 'Cancel',
          kind: 'secondary',
          onClick: close,
          testId: 'import-dtools-projects-preview-cancel',
        },
        {
          label: errors?.length ? 'Import anyway' : 'Import all',
          kind: 'primary',
          onClick: () => void handleImport(),
          disabled: projectIds.length === 0 || loading || hasImportStarted,
          loading: isStartingJob,
          testId: 'import-dtools-projects-preview-import',
        },
      ]}
    >
      <Stack gap={2} data-testid="import-dtools-projects-preview">
        <Typography variant="body2" color="text.secondary">
          {`Review ${projectIds.length} project(s) to import`}
        </Typography>
        {content}
      </Stack>
    </ModalStep>
  );
};
